import BrowseListSchema from '../../features/browse/BrowseListSchema';
import { SEO_DESCRIPTIONS } from '../../constants/seoDescriptions';
import { SITE_URL } from '../../utils/env';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountBoundary } from '../../features/settings/shared';
import { useUserStore } from '../../stores/userStore';
import {
  useBrowseSession,
  useBrowseContext,
  useBrowsePosition,
} from '../../features/browse/useBrowseList';
import { reviewColumns } from '../../features/browse/listModel';
import BrowseListState, { BrowseEmpty } from '../../features/browse/BrowseListState';
import BrowseShell, { BrowseSegments } from '../../features/browse/BrowseShell';
import BrowseFilters from '../../features/browse/BrowseFilters';
import BrowseReviewCard from '../../features/browse/BrowseReviewCard';
import { browseParams, reviewPath } from '../../features/browse/contracts';
import { BrowseInlineAd } from '../../features/browse/BrowseSponsored';
import { useSponsoredPlacements } from '../../features/ads/useSponsoredPlacements';
import { sponsoredBlocks } from '../../features/browse/sponsored';
import { useMediaQuery } from '@mui/material';
import BrowseLoadMore from '../../features/browse/BrowseLoadMore';
import { useBrowseStream } from '../../features/browse/useBrowseStream';
import SEO from '../../components/common/SEO';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE } from '../../constants/gaEnums';
export default function Reviews() {
  return <AccountBoundary component={ReviewsBoundary} />;
}
function ReviewsBoundary({ user }) {
  const { locale } = useWebLocale();
  const memory = useBrowseSession('reviews');
  if (useUserStore.getState().user?.id !== user?.id) return null;
  return <ReviewsView key={`${locale}:${memory.entry.version}`} {...memory} />;
}
function ReviewsView({ entry, returning }) {
  const [params, setParams] = useSearchParams();
  const { locale, t } = useWebLocale(),
    en = locale === 'en';
  const filters = browseParams(params, 'reviews');
  const tablet = useMediaQuery('(min-width:768px)');
  const desktop = useMediaQuery('(min-width:1280px)');
  const columns = reviewColumns(filters.view, tablet, desktop);
  const context = useBrowseContext(entry);
  context.tabs[filters.view] = filters.page;
  const args = { ...filters, size: columns * 10 };
  const identity = `${locale}:${entry.version}:${context.id}`;
  const query = useBrowseStream('reviews', args, identity);
  const position = useBrowsePosition({ entry, returning, ready: !query.isPending });

  const campaigns = useSponsoredPlacements(
    filters.view === 'feed' ? 'REVIEWS_FEED' : 'REVIEWS_ALBUMS',
  ).filter((campaign) => campaign.placements[0]?.format === 'INLINE_BANNER');
  const reviews = (query.data?.albums || []).filter((item) => item.content?.trim());
  const blocks = reviews.length ? sponsoredBlocks(reviews, campaigns, columns) : [];
  useTrackPageView({
    pageType: PAGE_TYPE.REVIEWS_MAIN,
    reviewsCategory: `${filters.region}_${filters.category}_reviews`,
  });
  const change = (next) => {
    context.tabs = {};
    setParams({
      ...filters,
      ...next,
      category: (next.sort || filters.sort) === 'likes' ? 'popular' : 'recent',
      page: '1',
    });
  };
  return (
    <BrowseShell
      active="reviews"
      swipe={{
        values: ['feed', 'albums'],
        value: filters.view,
        onChange: (view) => setParams({ ...filters, view, page: String(context.tabs[view] || 1) }),
      }}
    >
      <div {...position}>
        <SEO
          title={t('reviews')}
          description={SEO_DESCRIPTIONS.REVIEWS}
          url={`${SITE_URL}/reviews`}
        />
        <BrowseListSchema
          title={t('reviews')}
          items={reviews.map((item) => ({
            title: item.albumTitle,
            image: item.albumImageUrl,
            to: reviewPath(item),
          }))}
        />
        <div className="pp-browse-toolbar">
          <BrowseSegments
            options={[
              ['feed', en ? 'Review feed' : '리뷰 피드'],
              ['albums', en ? 'View by album' : '앨범으로 보기'],
            ]}
            value={filters.view}
            onChange={(view) =>
              setParams({ ...filters, view, page: String(context.tabs[view] || 1) })
            }
          />
          <BrowseFilters key={params.toString()} kind="reviews" value={filters} onChange={change} />
        </div>
        <BrowseListState query={query} kind="reviews">
          {!query.isError && !reviews.length && <BrowseEmpty kind="reviews" />}
          <div className={filters.view === 'feed' ? 'pp-browse-feeds' : 'pp-browse-grid'}>
            {blocks.flatMap((block, index) =>
              block.kind === 'inline'
                ? [
                    <div
                      key={`ad-${block.campaign.id}-${block.occurrence}`}
                      className="pp-browse-full"
                    >
                      <BrowseInlineAd campaign={block.campaign} />
                    </div>,
                  ]
                : block.tiles.map((entry) => (
                    <BrowseReviewCard
                      key={`${entry.item.reviewId}-${index}`}
                      item={entry.item}
                      view={filters.view}
                      first={index === 0}
                      last={index === blocks.length - 1 || blocks[index + 1]?.kind === 'inline'}
                    />
                  )),
            )}
          </div>
          <BrowseLoadMore
            key={JSON.stringify([identity, args])}
            query={query}
            restored={returning && entry.y !== undefined}
          />
        </BrowseListState>
      </div>
    </BrowseShell>
  );
}
